// src/components/forms/shared/BookingCardSurchargeList.tsx
// Displays booking surcharges as simple line items with reason and amount
// Reuses SummaryRow component for consistency with BookingSummary

import React from 'react';
import { Box } from '@mui/material';
import SummaryRow from './SummaryRow';
import { getCustomIcon } from '../../../utils/customIcons';

interface BookingSurchargeResponse {
  id: number;
  reason: string;
  amount: number;
}

interface BookingCardSurchargeListProps {
  surcharges: BookingSurchargeResponse[];
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const BookingCardSurchargeList: React.FC<BookingCardSurchargeListProps> = ({
  surcharges,
}) => {
  if (surcharges.length === 0) return null;

  return (
    <Box sx={{ width: '100%', maxWidth: '800px' }}>
      {surcharges.map((surcharge, idx) => {
        // Build subrows for this surcharge
        const subRows: Array<{
          label: string | React.ReactNode;
          value?: string | React.ReactNode;
          isTotal?: boolean;
          rowType?: 'default' | 'total';
        }> = [];

        // Single total row with the amount (empty label, bold value)
        subRows.push({
          label: '',
          value: formatCurrency(surcharge.amount),
          isTotal: true,
          rowType: 'total',
        });

        return (
          <Box
            key={surcharge.id}
            sx={{
              marginBottom: 0,
              '& > div': {
                borderLeft: 'none',
                pl: 0,
                borderRadius: 0,
                backgroundColor: 'transparent',
                '& > div': {
                  pt: '0.75rem',
                  mb: 0,
                },
                '& > div > div': {
                  pl: '0.75rem',
                  pr: 1.5,
                },
                '@media (max-width: 600px)': {
                  pl: 0,
                },
              },
            }}
          >
            <SummaryRow
              label={
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  {/* icon container */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {getCustomIcon('money', '2.5rem')}
                  </Box>
                  <span style={{ fontWeight: 600, fontSize: '2rem' }}>
                    {surcharge.reason}
                  </span>
                </Box>
              }
              value=""
              subRows={subRows}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default BookingCardSurchargeList;
